import React, { useState } from 'react';
import { Printer } from 'lucide-react';
import { currencySymbol, fmtNum } from '../utils/finance';
import { fmtMonth, fmtDate } from '../utils/dateFormat';

const ALL_COLUMNS = [
  { key: 'index', label: '#' },
  { key: 'date', label: 'Date' },
  { key: 'description', label: 'Description' },
  { key: 'business', label: 'Business' },
  { key: 'category', label: 'Category' },
  { key: 'amount', label: 'Amount' },
];

const DEFAULT_COLUMNS = ['index', 'date', 'description', 'business', 'category', 'amount'];

export default function ClaimStatement({ pettyCash, month, businesses }) {
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [showBalances, setShowBalances] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [showSignatures, setShowSignatures] = useState(true);

  const sym = currencySymbol(pettyCash.currency);

  const monthDate = new Date(month + '-01');
  const monthLabel = fmtMonth(month);

  const monthStart = month + '-01';
  const nextMonth = new Date(monthDate);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthStr = nextMonth.toISOString().slice(0, 7) + '-01';

  const topUpsBefore = pettyCash.topUps
    .filter((t) => t.date < monthStart)
    .reduce((s, t) => s + t.amount, 0);
  const expensesBefore = pettyCash.expenses
    .filter((e) => e.date < monthStart)
    .reduce((s, e) => s + e.amount, 0);
  const openingBalance = topUpsBefore - expensesBefore;

  const monthTopUps = pettyCash.topUps.filter(
    (t) => t.date >= monthStart && t.date < nextMonthStr
  );
  const monthExpenses = pettyCash.expenses
    .filter((e) => e.date >= monthStart && e.date < nextMonthStr)
    .sort((a, b) => a.date.localeCompare(b.date));

  const totalMonthTopUps = monthTopUps.reduce((s, t) => s + t.amount, 0);
  const totalMonthExpenses = monthExpenses.reduce((s, e) => s + e.amount, 0);
  const closingBalance = openingBalance + totalMonthTopUps - totalMonthExpenses;

  const getBusinessName = (id) => {
    if (!id) return 'General / NK';
    const biz = businesses.find((b) => b.id === id);
    return biz ? biz.name : id;
  };

  const byBusiness = {};
  for (const exp of monthExpenses) {
    const name = getBusinessName(exp.businessId);
    if (!byBusiness[name]) byBusiness[name] = 0;
    byBusiness[name] += exp.amount;
  }

  const byCategory = {};
  for (const exp of monthExpenses) {
    if (!byCategory[exp.category]) byCategory[exp.category] = 0;
    byCategory[exp.category] += exp.amount;
  }

  const toggleColumn = (key) => {
    if (key === 'amount') return; // amount is always shown
    setColumns((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const has = (key) => columns.includes(key);
  const visibleColCount = columns.length;

  const printStatement = () => {

    const headerCells = ALL_COLUMNS
      .filter((c) => has(c.key))
      .map((c) => `<th${c.key === 'amount' ? ' class="amount"' : ''}>${c.label}</th>`)
      .join('');

    const bodyRows = monthExpenses.map((e, i) => {
      const cells = [];
      if (has('index')) cells.push(`<td>${i + 1}</td>`);
      if (has('date')) cells.push(`<td>${e.date}</td>`);
      if (has('description')) cells.push(`<td>${e.description}</td>`);
      if (has('business')) cells.push(`<td>${getBusinessName(e.businessId)}</td>`);
      if (has('category')) cells.push(`<td>${e.category}</td>`);
      if (has('amount')) cells.push(`<td class="amount">${sym}${fmtNum(e.amount)}</td>`);
      return `<tr>${cells.join('')}</tr>`;
    }).join('');

    const totalColSpan = visibleColCount - 1;

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <meta name="format-detection" content="telephone=no, date=no, address=no, email=no">
        <meta name="color-scheme" content="light only">
        <title>Petty Cash Statement - ${monthLabel}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          body {
            font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            padding: 48px;
            color: #3A3A3A;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            border-bottom: 2px solid #7A8B8F;
            padding-bottom: 16px;
            margin-bottom: 32px;
          }
          .company {
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-size: 28px;
            color: #7A8B8F;
            letter-spacing: 2px;
            margin-bottom: 4px;
          }
          .title { font-size: 20px; font-weight: 400; color: #555; }
          .period { font-size: 14px; color: #6B7280; margin-top: 4px; }

          .balance-box {
            display: flex;
            gap: 24px;
            margin-bottom: 32px;
          }
          .bal-item {
            flex: 1;
            background: #F9F9F9;
            border: 1px solid #E5E7EB;
            border-radius: 8px;
            padding: 16px;
          }
          .bal-item.highlight { border-color: #7A8B8F; border-top: 3px solid #7A8B8F; }
          .bal-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #6B7280; }
          .bal-value { font-size: 22px; font-weight: 700; font-family: 'DM Mono', monospace; margin-top: 4px; }
          .bal-value.negative { color: #9B6B5E; }

          h2 { font-size: 16px; color: #7A8B8F; text-transform: uppercase; letter-spacing: 0.5px; margin: 24px 0 12px; border-bottom: 1px solid #E5E7EB; padding-bottom: 8px; }

          table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 24px; }
          th { text-align: left; padding: 8px 12px; background: #F9F9F9; color: #6B7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #E5E7EB; }
          td { padding: 8px 12px; border-bottom: 1px solid #F0F0F0; }
          .amount { text-align: right; font-family: 'DM Mono', monospace; font-weight: 500; }
          .total-row td { font-weight: 700; border-top: 2px solid #E5E7EB; background: #F9F9F9; }

          .summary-grid { display: flex; gap: 24px; margin-bottom: 24px; }
          .summary-col { flex: 1; }
          .summary-item { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #F0F0F0; font-size: 13px; }

          .footer {
            margin-top: 48px;
            display: flex;
            justify-content: space-between;
            padding-top: 24px;
          }
          .sig-line { border-top: 1px solid #3A3A3A; width: 200px; padding-top: 8px; text-align: center; color: #6B7280; font-size: 12px; }

          .generated { text-align: center; font-size: 11px; color: #6B7280; margin-top: 32px; }

          .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin-bottom: 24px; }
          .table-wrap table { margin-bottom: 0; }

          /* ── Tablet / mobile (≤768px) ── */
          @media (max-width: 768px) {
            body { padding: 20px; max-width: 100%; }
            .header { margin-bottom: 24px; padding-bottom: 12px; }
            .company { font-size: 22px; letter-spacing: 1.5px; }
            .title { font-size: 16px; }
            .period { font-size: 13px; }

            .balance-box { flex-wrap: wrap; gap: 12px; margin-bottom: 24px; }
            .bal-item { flex: 1 1 calc(50% - 6px); min-width: 0; padding: 12px; }
            .bal-label { font-size: 10px; }
            .bal-value { font-size: 18px; }

            h2 { font-size: 14px; margin: 20px 0 10px; }

            table { font-size: 12px; min-width: 480px; }
            th, td { padding: 6px 8px; }

            .summary-grid { flex-direction: column; gap: 16px; }
            .summary-item { font-size: 12px; }

            .footer { flex-direction: column; gap: 32px; margin-top: 32px; }
            .sig-line { width: 100%; }
          }

          /* ── Small phone (≤420px) ── */
          @media (max-width: 420px) {
            body { padding: 14px; }
            .balance-box { gap: 8px; }
            .bal-item { flex: 1 1 100%; padding: 10px; }
            .company { font-size: 20px; }
            table { font-size: 11px; min-width: 420px; }
          }

          @media print {
            body { padding: 24px; }
            .bal-item { break-inside: avoid; }
            .table-wrap { overflow: visible; }
            table { min-width: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company">UNIHUB</div>
          <div class="title">Petty Cash Claim Statement</div>
          <div class="period">${monthLabel}</div>
        </div>

        ${showBalances ? `
        <div class="balance-box">
          <div class="bal-item">
            <div class="bal-label">Opening Balance</div>
            <div class="bal-value">${sym}${fmtNum(openingBalance)}</div>
          </div>
          ${totalMonthTopUps > 0 ? `
          <div class="bal-item">
            <div class="bal-label">Top-ups</div>
            <div class="bal-value">+${sym}${fmtNum(totalMonthTopUps)}</div>
          </div>` : ''}
          <div class="bal-item">
            <div class="bal-label">Total Expenses</div>
            <div class="bal-value">${sym}${fmtNum(totalMonthExpenses)}</div>
          </div>
          <div class="bal-item highlight">
            <div class="bal-label">Closing Balance</div>
            <div class="bal-value ${closingBalance < 0 ? 'negative' : ''}">${sym}${fmtNum(closingBalance)}</div>
          </div>
        </div>` : ''}

        ${monthTopUps.length > 0 ? `
        <h2>Top-ups</h2>
        <div class="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Note</th><th class="amount">Amount</th></tr></thead>
            <tbody>
              ${monthTopUps.map((t) => `
                <tr>
                  <td>${t.date}</td>
                  <td>${t.note || '-'}</td>
                  <td class="amount">+${sym}${fmtNum(t.amount)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>` : ''}

        <h2>Expenses</h2>
        ${monthExpenses.length === 0 ? '<p style="color:#6B7280;font-size:14px;">No expenses recorded for this period.</p>' : `
        <div class="table-wrap">
          <table>
            <thead><tr>${headerCells}</tr></thead>
            <tbody>
              ${bodyRows}
              <tr class="total-row">
                <td colspan="${totalColSpan}">Total</td>
                <td class="amount">${sym}${fmtNum(totalMonthExpenses)}</td>
              </tr>
            </tbody>
          </table>
        </div>`}

        ${showSummary ? `
        <h2>Summary</h2>
        <div class="summary-grid">
          ${has('business') ? `
          <div class="summary-col">
            <strong style="font-size:12px;color:#6B7280;text-transform:uppercase;">By Business</strong>
            ${Object.entries(byBusiness).map(([name, amt]) => `
              <div class="summary-item">
                <span>${name}</span>
                <span class="amount">${sym}${fmtNum(amt)}</span>
              </div>
            `).join('')}
          </div>` : ''}
          ${has('category') ? `
          <div class="summary-col">
            <strong style="font-size:12px;color:#6B7280;text-transform:uppercase;">By Category</strong>
            ${Object.entries(byCategory).map(([cat, amt]) => `
              <div class="summary-item">
                <span>${cat}</span>
                <span class="amount">${sym}${fmtNum(amt)}</span>
              </div>
            `).join('')}
          </div>` : ''}
        </div>` : ''}

        ${showSignatures ? `
        <div class="footer">
          <div class="sig-line">Prepared By</div>
          <div class="sig-line">Approved By</div>
        </div>` : ''}

        <div class="generated">Generated ${fmtDate(new Date())} - UniHub Financial Intelligence</div>
      </body>
      </html>
    `);
    win.document.close();
    win.print();
  };

  return (
    <div className="statement-preview">
      <div className="statement-card">
        <div className="statement-card-header">
          <span className="statement-month-title">{monthLabel}</span>
        </div>

        {/* Column & section toggles */}
        <div className="stmt-options">
          <div className="stmt-options-group">
            <span className="stmt-options-label">Columns</span>
            <div className="stmt-toggle-grid">
              {ALL_COLUMNS.map((c) => (
                <label key={c.key} className="stmt-toggle-item">
                  <input
                    type="checkbox"
                    checked={columns.includes(c.key)}
                    onChange={() => toggleColumn(c.key)}
                    disabled={c.key === 'amount'}
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="stmt-options-group">
            <span className="stmt-options-label">Sections</span>
            <div className="stmt-toggle-grid">
              <label className="stmt-toggle-item">
                <input type="checkbox" checked={showBalances} onChange={() => setShowBalances(!showBalances)} />
                <span>Balances</span>
              </label>
              <label className="stmt-toggle-item">
                <input type="checkbox" checked={showSummary} onChange={() => setShowSummary(!showSummary)} />
                <span>Summary</span>
              </label>
              <label className="stmt-toggle-item">
                <input type="checkbox" checked={showSignatures} onChange={() => setShowSignatures(!showSignatures)} />
                <span>Signatures</span>
              </label>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="stmt-live-preview">
          <div className="stmt-preview-label">Preview</div>

          {showBalances && (
            <div className="stmt-preview-balances">
              <div className="stmt-bal">
                <span className="stmt-bal-label">Opening</span>
                <span className="stmt-bal-value mono">{sym}{fmtNum(openingBalance)}</span>
              </div>
              {totalMonthTopUps > 0 && (
                <div className="stmt-bal">
                  <span className="stmt-bal-label">Top-ups</span>
                  <span className="stmt-bal-value mono positive">+{sym}{fmtNum(totalMonthTopUps)}</span>
                </div>
              )}
              <div className="stmt-bal">
                <span className="stmt-bal-label">Expenses ({monthExpenses.length} items)</span>
                <span className="stmt-bal-value mono spent">-{sym}{fmtNum(totalMonthExpenses)}</span>
              </div>
              <div className="stmt-bal closing">
                <span className="stmt-bal-label">Closing</span>
                <span className={`stmt-bal-value mono ${closingBalance >= 0 ? 'positive' : 'negative'}`}>
                  {sym}{fmtNum(closingBalance)}
                </span>
              </div>
            </div>
          )}

          {monthExpenses.length > 0 && (
            <div className="table-scroll">
              <table className="data-table stmt-preview-table">
                <thead>
                  <tr>
                    {has('index') && <th>#</th>}
                    {has('date') && <th>Date</th>}
                    {has('description') && <th>Description</th>}
                    {has('business') && <th>Business</th>}
                    {has('category') && <th>Category</th>}
                    {has('amount') && <th style={{ textAlign: 'right' }}>Amount</th>}
                  </tr>
                </thead>
                <tbody>
                  {monthExpenses.map((e, i) => (
                    <tr key={e.id || i}>
                      {has('index') && <td>{i + 1}</td>}
                      {has('date') && <td>{e.date}</td>}
                      {has('description') && <td>{e.description}</td>}
                      {has('business') && <td>{getBusinessName(e.businessId)}</td>}
                      {has('category') && <td>{e.category}</td>}
                      {has('amount') && <td className="mono" style={{ textAlign: 'right' }}>{sym}{fmtNum(e.amount)}</td>}
                    </tr>
                  ))}
                  <tr className="highlight-row">
                    <td colSpan={columns.filter(c => c !== 'amount').length}>Total</td>
                    {has('amount') && <td className="mono" style={{ textAlign: 'right' }}>{sym}{fmtNum(totalMonthExpenses)}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {showSummary && Object.keys(byBusiness).length > 0 && (
            <div className="stmt-preview-summary">
              {has('business') && (
                <div className="stmt-preview-summary-col">
                  <span className="stmt-options-label">By Business</span>
                  {Object.entries(byBusiness).sort((a, b) => b[1] - a[1]).map(([name, amt]) => (
                    <div key={name} className="stmt-breakdown-row">
                      <span>{name}</span>
                      <span className="mono">{sym}{fmtNum(amt)}</span>
                    </div>
                  ))}
                </div>
              )}
              {has('category') && (
                <div className="stmt-preview-summary-col">
                  <span className="stmt-options-label">By Category</span>
                  {Object.entries(byCategory).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <div key={cat} className="stmt-breakdown-row">
                      <span>{cat}</span>
                      <span className="mono">{sym}{fmtNum(amt)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {showSignatures && (
            <div className="stmt-preview-sigs">
              <div className="stmt-sig-line">Prepared By</div>
              <div className="stmt-sig-line">Approved By</div>
            </div>
          )}
        </div>

        <button className="btn btn-primary" onClick={printStatement} style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
          <Printer size={16} /> Print / Export Statement
        </button>
      </div>
    </div>
  );
}
